import { useState, type FormEvent } from 'react';
import { Copy } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { WEEKDAY_SHORT } from '@/features/teacher/components/timetable/timetableCopy';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { CopyTimetableResult, TimetableRange } from '@/types/timetableAdmin';
import { instituteTimetableApi } from '../api/timetable.api';
import { describeTimetableRange } from '../timetableRange';

export interface CopyTimetableModalProps {
  classes: Array<{ uuid: string; name: string; sections: Array<{ uuid: string; name: string }> }>;
  /** The section on screen and the timetable in force that week. Null when no timetable covers it. */
  source: { sectionUuid: string; sectionName: string; timetable: TimetableRange | null };
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Copies the timetable on screen — its dates and its lessons — into other
 * sections, so 9-A's week is arranged once and handed to 9-B and 9-C.
 *
 * Each section gets a timetable of its own with the same dates. A lesson a
 * section cannot take is skipped rather than failing the copy: its class may
 * not study that subject, nobody may teach it there yet, or its teacher may be
 * elsewhere in that period. Those are listed afterwards in the server's own
 * words, so the office knows exactly which cells to fill by hand.
 */
export function CopyTimetableModal({ classes, source, onClose, onSaved }: CopyTimetableModalProps) {
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [isCopying, setIsCopying] = useState(false);
  const [result, setResult] = useState<CopyTimetableResult | null>(null);

  const groups = classes
    .map((item) => ({
      ...item,
      sections: item.sections.filter((section) => section.uuid !== source.sectionUuid),
    }))
    .filter((item) => item.sections.length > 0);

  function toggle(uuid: string) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  async function copy(event: FormEvent) {
    event.preventDefault();
    if (!source.timetable || chosen.size === 0 || isCopying) return;

    setIsCopying(true);
    try {
      const copied = await instituteTimetableApi.copyTimetable(source.timetable.uuid, [...chosen]);
      const skipped = copied.results.reduce((total, row) => total + row.skipped.length, 0);
      toast.success(
        `Copied to ${copied.results.length === 1 ? '1 section' : `${copied.results.length} sections`}`,
        skipped === 0
          ? 'Every lesson went across.'
          : `${skipped} ${skipped === 1 ? 'lesson was' : 'lessons were'} skipped — see why below.`,
      );
      onSaved();
      setResult(copied);
    } catch (caught) {
      toast.error(
        'Could not copy the timetable',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsCopying(false);
    }
  }

  if (result) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Timetable copied"
        description={`From ${result.source.section}, ${describeTimetableRange(result.source)}.`}
        size="md"
        footer={
          <Button
            variant="primary"
            size="md"
            onClick={onClose}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            Done
          </Button>
        }
      >
        <ul className="flex flex-col gap-3 py-1">
          {result.results.map((row) => (
            <li
              key={row.section.uuid}
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1322] p-3.5"
            >
              <p className="text-sm font-black text-slate-800 dark:text-white">
                {row.section.displayName}
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {row.copied} {row.copied === 1 ? 'lesson' : 'lessons'} copied
                {row.skipped.length > 0 && `, ${row.skipped.length} skipped`}
              </p>
              {row.skipped.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                  {row.skipped.map((lesson) => (
                    <li
                      key={`${lesson.dayOfWeek}-${lesson.periodNumber}`}
                      className="text-xs text-slate-600 dark:text-slate-300"
                    >
                      <span className="font-bold">
                        {WEEKDAY_SHORT[lesson.dayOfWeek]}, period {lesson.periodNumber} (
                        {lesson.subject}):
                      </span>{' '}
                      {lesson.reason}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Copy timetable to other sections"
      description="Each section you choose gets its own timetable with the same dates and lessons. A section that already has a timetable on any of those dates cannot be copied to."
      size="md"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isCopying}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="copy-timetable-form"
            isLoading={isCopying}
            disabled={!source.timetable || chosen.size === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            {chosen.size > 1 ? `Copy to ${chosen.size} sections` : 'Copy timetable'}
          </Button>
        </>
      }
    >
      <form id="copy-timetable-form" onSubmit={copy} className="flex flex-col gap-4 py-1">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1322] p-3.5 flex items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400">
            <Copy className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <span className="lbl block text-slate-500 dark:text-slate-400">Copying from</span>
            <p className="text-sm font-black text-slate-800 dark:text-white truncate">
              {source.sectionName}
              {source.timetable ? ` · ${describeTimetableRange(source.timetable)}` : ''}
            </p>
          </div>
        </div>

        {!source.timetable ? (
          <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
            No timetable applies to the week on screen, so there is nothing to copy. Move to a week
            that has one, or create a timetable first.
          </p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
            There are no other sections to copy to.
          </p>
        ) : (
          <fieldset className="flex flex-col gap-3">
            <legend className="lbl mb-1 text-slate-500 dark:text-slate-400">Copy to</legend>
            {groups.map((group) => (
              <div key={group.uuid} className="flex flex-col gap-1.5">
                <p className="text-xs font-bold text-slate-700 dark:text-slate-200">{group.name}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {group.sections.map((section) => (
                    <Checkbox
                      key={section.uuid}
                      label={`${group.name} - ${section.name}`}
                      checked={chosen.has(section.uuid)}
                      onChange={() => toggle(section.uuid)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </fieldset>
        )}
      </form>
    </Modal>
  );
}
